export const M4_UNIT_10 = {
  id: "m4-u10-the-body",
  title: "Unidad 10: The Body / El Cuerpo",
  topic: "Learn to describe physical features! Talk about your appearance and others' with have/has and to be. Master body parts vocabulary and adjectives. Essential for descriptions, medical situations, and daily conversations!",
  iconType: "body",
  grammarImage: "/grammar/grammar-m4-u10.jpg",
  grammarText: "HAVE/HAS + adjective + body part (I have brown eyes, She has long hair). MY/YOUR/HIS/HER + body part + IS/ARE + adjective (My eyes are blue, Her hair is blonde). Recuerda: I/You/We/They = HAVE | He/She/It = HAS. Singular = IS | Plural = ARE.",
  
  sets: [
    // SET 1: Basic Body Parts Vocabulary
    {
      id: "m4-u10-set1",
      title: "Set 1: Face & Head Vocabulary",
      questions: [
        {
          type: "match",
          q: "Match body part to Spanish:",
          pairs: [
            { left: "eyes", right: "ojos" },
            { left: "hair", right: "cabello/pelo" },
            { left: "mouth", right: "boca" }
          ],
          a: "completed",
          tip: "These are the most common facial features you'll describe every day!"
        },
        {
          type: "mc",
          q: "'I have brown ___.' (Which word?)",
          options: ["eyes", "eye", "hairs"],
          a: "eyes",
          tip: "Eyes = always plural when talking about your OWN eyes! 'I have two eyes' = 'I have eyes' ✓"
        },
        {
          type: "input",
          q: "Complete: 'She has beautiful ___.' (cabello)",
          a: "hair"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo ojos azules'",
          words: ["I", "have", "blue", "eyes."],
          a: "I have blue eyes."
        },
        {
          type: "mc",
          q: "Which is correct? 'My friend has beautiful ___.'",
          options: ["eyes", "eye", "mouth"],
          a: "eyes",
          tip: "Beautiful eyes = very common compliment! 'You have beautiful eyes' = classic phrase!"
        },
        {
          type: "input",
          q: "Complete: 'My ___ is small.' (nariz)",
          a: "nose"
        },
        {
          type: "mc",
          q: "'Her ___ are big and beautiful.'",
          options: ["lips (labios)", "lip", "mouth"],
          a: "lips (labios)",
          tip: "Lips = always plural! Just like eyes. 'I have lips' ✓ not 'I have lip' ❌"
        },
        {
          type: "order",
          q: "Ordena: 'Ella tiene cabello largo'",
          words: ["She", "has", "long", "hair."],
          a: "She has long hair.",
          successtip: "✓ LONG HAIR! Adjective BEFORE noun! 📏 'Long hair' ✓ not 'hair long' ❌. This is English word order!"
        },
        {
          type: "input",
          q: "Complete: 'He has a big ___.' (boca)",
          a: "mouth"
        },
        {
          type: "mc",
          q: "Identify: 'My ___ are white.'",
          options: ["teeth (dientes)", "tooth", "tooths"],
          a: "teeth (dientes)",
          tip: "Tooth (singular) → Teeth (plural - irregular)! Like foot → feet, child → children!"
        }
      ]
    },

    // SET 2: Body Parts - Upper Body
    {
      id: "m4-u10-set2",
      title: "Set 2: Arms, Hands & Upper Body",
      questions: [
        {
          type: "match",
          q: "Match body part to Spanish:",
          pairs: [
            { left: "hand", right: "mano" },
            { left: "arm", right: "brazo" },
            { left: "fingers", right: "dedos (de la mano)" }
          ],
          a: "completed",
          tip: "Upper body = important for describing actions and gestures!"
        },
        {
          type: "mc",
          q: "'I have big ___.' (manos)",
          options: ["hands", "hand", "fingers"],
          a: "hands",
          tip: "Hands = plural! You have TWO hands. 'I have hands' ✓"
        },
        {
          type: "input",
          q: "Complete: 'She has long ___.' (brazos)",
          a: "arms"
        },
        {
          type: "order",
          q: "Ordena: 'Él tiene dedos largos'",
          words: ["He", "has", "long", "fingers."],
          a: "He has long fingers."
        },
        {
          type: "mc",
          q: "Which form? 'My friend ___ strong arms.'",
          options: ["has (verb+s)", "have (base verb)", "is having (continuous)"],
          a: "has (verb+s)",
          tip: "He/She/It + HAS! Physical features = permanent = use HAVE/HAS, not continuous!"
        },
        {
          type: "input",
          q: "Complete: 'They have big ___.' (hombros)",
          a: "shoulders"
        },
        {
          type: "mc",
          q: "'My ___ are strong.' (plural - brazos)",
          options: ["arms", "arm", "hand"],
          a: "arms",
          tip: "Arms = plural! ARE (not IS) because plural! My armS ARE ✓"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo manos pequeñas'",
          words: ["I", "have", "small", "hands."],
          a: "I have small hands.",
          successtip: "✓ SMALL HANDS! Common description! 🙌 Adjective + noun order. 'Small hands' = hands pequeñas!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ beautiful fingers.' (have/has)",
          a: "has"
        },
        {
          type: "mc",
          q: "Identify: 'Her ___ is big.' (pecho - singular)",
          options: ["chest", "chests", "shoulder"],
          a: "chest",
          tip: "Chest = singular! IS (not ARE). 'Her chest IS' ✓"
        }
      ]
    },

    // SET 3: Using HAVE/HAS with Adjectives
    {
      id: "m4-u10-set3",
      title: "Set 3: HAVE vs HAS Practice",
      questions: [
        {
          type: "match",
          q: "Match subject to correct form:",
          pairs: [
            { left: "I/You/We/They", right: "HAVE" },
            { left: "He/She/It", right: "HAS" },
            { left: "My friend", right: "HAS (he or she)" }
          ],
          a: "completed",
          tip: "This is THE rule! I/You/We/They = HAVE. He/She/It = HAS. Memorize it!"
        },
        {
          type: "mc",
          q: "'I ___ brown eyes.' (Which form?)",
          options: ["have", "has", "am"],
          a: "have",
          tip: "I = HAVE! Always! 'I have' ✓ Never 'I has' ❌"
        },
        {
          type: "input",
          q: "Complete: 'She ___ blonde hair.' (have/has)",
          a: "has"
        },
        {
          type: "order",
          q: "Ordena: 'Mi amigo tiene ojos verdes'",
          words: ["My", "friend", "has", "green", "eyes."],
          a: "My friend has green eyes."
        },
        {
          type: "mc",
          q: "Which verb form? 'They ___ big hands.'",
          options: ["have (plural form)", "has (singular form)", "is (be verb)"],
          a: "have (plural form)",
          tip: "They = HAVE! Plural subjects = HAVE. 'They have' ✓"
        },
        {
          type: "input",
          q: "Complete: 'We ___ small feet.' (have/has)",
          a: "have"
        },
        {
          type: "mc",
          q: "'He ___ a big nose.' (Which form?)",
          options: ["has", "have", "is"],
          a: "has",
          tip: "He = HAS! 'He has' ✓ Never 'He have' ❌"
        },
        {
          type: "order",
          q: "Ordena: 'Ellos tienen labios grandes'",
          words: ["They", "have", "big", "lips."],
          a: "They have big lips.",
          successtip: "✓ THEY HAVE! Plural! 👥 They/We = HAVE (no -s). He/She = HAS (with -s). Don't confuse!"
        },
        {
          type: "input",
          q: "Complete: 'My sister ___ beautiful eyes.' (have/has)",
          a: "has"
        },
        {
          type: "mc",
          q: "ERROR: 'She have brown eyes' - Correction:",
          options: ["'She has brown eyes' (he/she = HAS)", "'She have brown eyes' is correct", "'She is have brown eyes'"],
          a: "'She has brown eyes' (he/she = HAS)",
          tip: "She = HAS! 'She has' ✓ This is the MOST common error for beginners!"
        }
      ]
    },

    // SET 4: Describing with TO BE
    {
      id: "m4-u10-set4",
      title: "Set 4: Using IS/ARE for Descriptions",
      questions: [
        {
          type: "match",
          q: "Match structure to example:",
          pairs: [
            { left: "My + singular body part", right: "My hair IS blonde" },
            { left: "My + plural body part", right: "My eyes ARE blue" },
            { left: "Her + singular body part", right: "Her nose IS small" }
          ],
          a: "completed",
          tip: "Singular = IS. Plural = ARE. This is crucial for correct sentences!"
        },
        {
          type: "mc",
          q: "'My eyes ___ blue.' (singular or plural?)",
          options: ["are (plural)", "is (singular)", "has"],
          a: "are (plural)",
          tip: "Eyes = PLURAL! My eyeS ARE ✓ Never 'My eyes is' ❌"
        },
        {
          type: "input",
          q: "Complete: 'Her hair ___ blonde.' (is/are)",
          a: "is"
        },
        {
          type: "order",
          q: "Ordena: 'Mis labios son grandes'",
          words: ["My", "lips", "are", "big."],
          a: "My lips are big."
        },
        {
          type: "mc",
          q: "Which form? 'His nose ___ small.'",
          options: ["is (singular)", "are (plural)", "has"],
          a: "is (singular)",
          tip: "Nose = singular! His nose IS ✓ One nose = IS!"
        },
        {
          type: "input",
          q: "Complete: 'My teeth ___ white.' (is/are)",
          a: "are"
        },
        {
          type: "mc",
          q: "'Your hair ___ beautiful.' (Which form?)",
          options: ["is", "are", "has"],
          a: "is",
          tip: "Hair = SINGULAR in English! 'Hair is' ✓ even though in Spanish 'cabellos' can be plural!"
        },
        {
          type: "order",
          q: "Ordena: 'Su cabello es largo y hermoso'",
          words: ["Her", "hair", "is", "long", "and", "beautiful."],
          a: "Her hair is long and beautiful.",
          successtip: "✓ TWO ADJECTIVES! Using 'and'! ➕ Long AND beautiful = common pattern! Can describe with multiple adjectives!"
        },
        {
          type: "input",
          q: "Complete: 'Their eyes ___ green.' (is/are)",
          a: "are"
        },
        {
          type: "mc",
          q: "ERROR: 'My eyes is blue' - Problem:",
          options: ["Should be 'My eyes ARE blue' (eyes = plural)", "Should be 'My eye is blue'", "It's correct"],
          a: "Should be 'My eyes ARE blue' (eyes = plural)",
          tip: "Plural noun (eyes) = plural verb (ARE)! This is BASIC agreement!"
        }
      ]
    },

    // SET 5: Lower Body Vocabulary
    {
      id: "m4-u10-set5",
      title: "Set 5: Legs, Feet & Lower Body",
      questions: [
        {
          type: "match",
          q: "Match body part to Spanish:",
          pairs: [
            { left: "leg", right: "pierna" },
            { left: "foot", right: "pie (singular)" },
            { left: "feet", right: "pies (plural)" }
          ],
          a: "completed",
          tip: "Foot → Feet = irregular plural! Like tooth → teeth!"
        },
        {
          type: "mc",
          q: "'I have long ___.' (piernas)",
          options: ["legs", "leg", "foots"],
          a: "legs",
          tip: "Legs = plural! You have TWO legs. 'I have legs' ✓"
        },
        {
          type: "input",
          q: "Complete: 'She has small ___.' (pies - plural!)",
          a: "feet"
        },
        {
          type: "order",
          q: "Ordena: 'Él tiene rodillas fuertes'",
          words: ["He", "has", "strong", "knees."],
          a: "He has strong knees."
        },
        {
          type: "mc",
          q: "Which is correct plural? 'My ___ hurt.'",
          options: ["feet (irregular plural)", "foots", "foot"],
          a: "feet (irregular plural)",
          successtip: "✓ FEET! Irregular! 🦶🦶 Foot (1) → Feet (2+). Like man → men, woman → women. NOT 'foots' ❌!"
        },
        {
          type: "input",
          q: "Complete: 'They have big ___.' (dedos del pie)",
          a: "toes"
        },
        {
          type: "mc",
          q: "'My ___ are strong.' (piernas - plural)",
          options: ["legs", "leg", "knee"],
          a: "legs",
          tip: "Legs = plural! My legS ARE ✓ Plural subject = plural verb (ARE)!"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo pies pequeños'",
          words: ["I", "have", "small", "feet."],
          a: "I have small feet."
        },
        {
          type: "input",
          q: "Complete: 'Her ___ is strong.' (pierna - singular)",
          a: "leg"
        },
        {
          type: "mc",
          q: "Identify: 'His ___ hurt.' (rodillas)",
          options: ["knees", "knee", "legs"],
          a: "knees",
          tip: "Knees = plural! You have TWO knees. 'My knees hurt' = common phrase when you have pain!"
        }
      ]
    },

    // SET 6: Adjectives for Physical Features
    {
      id: "m4-u10-set6",
      title: "Set 6: Common Adjectives",
      questions: [
        {
          type: "match",
          q: "Match adjective to Spanish:",
          pairs: [
            { left: "long", right: "largo/a" },
            { left: "short", right: "corto/a" },
            { left: "big", right: "grande" }
          ],
          a: "completed",
          tip: "These adjectives work for ANY body part! Super useful!"
        },
        {
          type: "mc",
          q: "'She has ___ hair.' (largo)",
          options: ["long", "short", "big"],
          a: "long",
          tip: "Long hair = cabello largo! Very common description!"
        },
        {
          type: "input",
          q: "Complete: 'He has ___ eyes.' (grande)",
          a: "big"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo labios pequeños'",
          words: ["I", "have", "small", "lips."],
          a: "I have small lips."
        },
        {
          type: "mc",
          q: "Which adjective? 'My friend has ___ hair.' (corto)",
          options: ["short", "long", "beautiful"],
          a: "short",
          tip: "Short hair = cabello corto! Common for men and some women!"
        },
        {
          type: "input",
          q: "Complete: 'Her eyes are ___.' (hermoso)",
          a: "beautiful"
        },
        {
          type: "mc",
          q: "'They have ___ legs.' (largo)",
          options: ["long", "short", "small"],
          a: "long",
          tip: "Long legs = piernas largas! Common compliment!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella tiene ojos pequeños'",
          words: ["She", "has", "small", "eyes."],
          a: "She has small eyes.",
          successtip: "✓ SMALL EYES! Physical description! 👀 Remember: adjective BEFORE noun in English! 'Small eyes' ✓"
        },
        {
          type: "input",
          q: "Complete: 'My lips are ___ and beautiful.' (grande)",
          a: "big"
        },
        {
          type: "mc",
          q: "Identify: 'His nose is ___.' (pequeño)",
          options: ["small", "big", "long"],
          a: "small",
          tip: "Small nose = nariz pequeña! Common description!"
        }
      ]
    },

    // SET 7: Hair Colors & Eye Colors
    {
      id: "m4-u10-set7",
      title: "Set 7: Colors for Hair & Eyes",
      questions: [
        {
          type: "match",
          q: "Match color to Spanish:",
          pairs: [
            { left: "blonde", right: "rubio/a" },
            { left: "brown", right: "café/castaño" },
            { left: "black", right: "negro/a" }
          ],
          a: "completed",
          tip: "These are the MOST common hair and eye colors you'll use!"
        },
        {
          type: "mc",
          q: "'I have ___ hair.' (rubio)",
          options: ["blonde", "brown", "black"],
          a: "blonde",
          tip: "Blonde hair = cabello rubio! Very common in Canada!"
        },
        {
          type: "input",
          q: "Complete: 'She has ___ eyes.' (café)",
          a: "brown"
        },
        {
          type: "order",
          q: "Ordena: 'Él tiene cabello negro'",
          words: ["He", "has", "black", "hair."],
          a: "He has black hair."
        },
        {
          type: "mc",
          q: "Which color? 'My eyes are ___.' (azul)",
          options: ["blue", "green", "brown"],
          a: "blue",
          successtip: "✓ BLUE EYES! Common! 💙 Blue, green, brown = most common eye colors to describe!"
        },
        {
          type: "input",
          q: "Complete: 'They have ___ hair.' (castaño)",
          a: "brown"
        },
        {
          type: "mc",
          q: "'Her eyes are ___.' (verde)",
          options: ["green", "blue", "brown"],
          a: "green",
          tip: "Green eyes = ojos verdes! Less common but beautiful!"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo ojos azules y cabello rubio'",
          words: ["I", "have", "blue", "eyes", "and", "blonde", "hair."],
          a: "I have blue eyes and blonde hair.",
          successtip: "✓ COMBINING FEATURES! Natural description! 👁️💛 'I have X eyes and Y hair' = common pattern!"
        },
        {
          type: "input",
          q: "Complete: 'His hair is ___.' (negro)",
          a: "black"
        },
        {
          type: "mc",
          q: "Identify: 'My hair is ___.' (castaño oscuro)",
          options: ["dark brown", "light brown", "blonde"],
          a: "dark brown",
          tip: "Dark brown = castaño oscuro! Light brown = castaño claro! You can combine adjectives!"
        }
      ]
    },

    // SET 8: Complete Descriptions
    {
      id: "m4-u10-set8",
      title: "Set 8: Full Body Descriptions",
      questions: [
        {
          type: "mc",
          q: "'My friend ___ beautiful eyes and long hair.'",
          options: ["has", "have", "is"],
          a: "has",
          tip: "'My friend' = he or she = HAS! Can list multiple features with 'and'!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ blonde hair and blue eyes.' (have/has)",
          a: "has"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo cabello castaño, ojos verdes y nariz pequeña'",
          words: ["I", "have", "brown", "hair,", "green", "eyes,", "and", "a", "small", "nose."],
          a: "I have brown hair, green eyes, and a small nose.",
          successtip: "✓ FULL DESCRIPTION! Multiple features! 🎨 List with commas + 'and' before last item. This is natural English!"
        },
        {
          type: "mc",
          q: "Which is correct? 'He ___ long legs and big hands.'",
          options: ["has", "have", "is"],
          a: "has",
          tip: "He = HAS! Even with multiple features, still use HAS (not have)!"
        },
        {
          type: "input",
          q: "Complete: 'They ___ beautiful eyes and pretty lips.' (have/has)",
          a: "have"
        },
        {
          type: "mc",
          q: "'My sister ___ long hair, green eyes, and small feet.'",
          options: ["has", "have", "is"],
          a: "has",
          tip: "My sister = she = HAS! List multiple features naturally!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella tiene ojos hermosos, labios grandes y cabello largo'",
          words: ["She", "has", "beautiful", "eyes,", "big", "lips,", "and", "long", "hair."],
          a: "She has beautiful eyes, big lips, and long hair."
        },
        {
          type: "input",
          q: "Complete: 'We ___ brown eyes and black hair.' (have/has)",
          a: "have"
        },
        {
          type: "mc",
          q: "Full description: 'I ___ blonde hair. My eyes ___ blue. My lips ___ big.'",
          options: ["have, are, are", "has, is, are", "have, is, is"],
          a: "have, are, are",
          tip: "I HAVE hair. My eyeS ARE (plural). My lipS ARE (plural). Mix structures naturally!"
        },
        {
          type: "mc",
          q: "Natural description: 'My friend ___ very beautiful. She ___ long blonde hair and blue eyes. Her lips ___ big and pretty.'",
          options: ["is, has, are", "has, has, is", "is, have, are"],
          a: "is, has, are",
          successtip: "✓ MIXING TO BE + HAVE! Natural! 🎯 'She IS beautiful' (to be). 'She HAS hair/eyes' (have). 'Her lips ARE' (to be). This IS fluency!"
        }
      ]
    },

    // SET 9: Real Situations - Describing People
    {
      id: "m4-u10-set9",
      title: "Set 9: Describing Family & Friends",
      questions: [
        {
          type: "mc",
          q: "'My daughter ___ curly brown hair and green eyes. She ___ beautiful!'",
          options: ["has, is", "have, is", "has, are"],
          a: "has, is",
          tip: "My daughter = she = HAS. She IS beautiful (to be for general quality)!"
        },
        {
          type: "input",
          q: "Complete: 'My son ___ very tall. He ___ long legs.' (is/has)",
          a: "is, has"
        },
        {
          type: "order",
          q: "Ordena: 'Mi hermana es muy bonita - tiene ojos azules y cabello rubio'",
          words: ["My", "sister", "is", "very", "pretty.", "She", "has", "blue", "eyes", "and", "blonde", "hair."],
          a: "My sister is very pretty. She has blue eyes and blonde hair.",
          successtip: "✓ TWO SENTENCES! Natural break! 💬 'She IS pretty' (opinion). 'She HAS features' (description). This is how natives describe!"
        },
        {
          type: "mc",
          q: "'My friend ___ from Mexico. She ___ dark brown hair and brown eyes.'",
          options: ["is, has", "has, has", "is, is"],
          a: "is, has",
          tip: "She IS from (to be for origin). She HAS features (have for physical description)!"
        },
        {
          type: "input",
          q: "Complete: 'My brother ___ very handsome. He ___ black hair and green eyes.' (is/has)",
          a: "is, has"
        },
        {
          type: "mc",
          q: "'My parents ___ older now. They ___ gray hair.'",
          options: ["are, have", "is, has", "are, has"],
          a: "are, have",
          successtip: "✓ PARENTS! Plural! 👨👩 They ARE (plural). They HAVE (plural). Gray hair = cabello gris/cano!"
        },
        {
          type: "order",
          q: "Ordena: 'Mi esposo tiene 35 años - tiene cabello negro y ojos cafés'",
          words: ["My", "husband", "is", "35", "years", "old.", "He", "has", "black", "hair", "and", "brown", "eyes."],
          a: "My husband is 35 years old. He has black hair and brown eyes."
        },
        {
          type: "input",
          q: "Complete: 'My baby ___ very small. She ___ beautiful blue eyes.' (is/has)",
          a: "is, has"
        },
        {
          type: "mc",
          q: "'My grandmother ___ 80 years old. She ___ short gray hair and brown eyes. She ___ still beautiful!'",
          options: ["is, has, is", "has, has, is", "is, have, are"],
          a: "is, has, is",
          tip: "She IS 80 (age). She HAS hair/eyes (features). She IS beautiful (quality)!"
        },
        {
          type: "mc",
          q: "Describing yourself: 'I ___ from Colombia. I ___ 28 years old. I ___ brown eyes and black hair.'",
          options: ["am, am, have", "am, have, have", "have, am, have"],
          a: "am, am, have",
          successtip: "✓ SELF INTRODUCTION! Complete! 🙋 'I AM from' (origin). 'I AM years old' (age). 'I HAVE features' (body). This is a perfect intro!"
        }
      ]
    },

    // SET 10: Medical & Practical Situations
    {
      id: "m4-u10-set10",
      title: "Set 10: Medical & ID Descriptions",
      questions: [
        {
          type: "mc",
          q: "Doctor: 'What color ___ your eyes?' You: 'They ___ brown.'",
          options: ["are, are", "is, are", "are, is"],
          a: "are, are",
          tip: "Eyes = plural! 'What color ARE (plural)' + 'They ARE (plural)'. Medical forms ask this!"
        },
        {
          type: "input",
          q: "Complete: Form asks 'Hair color: ___' You write: '___' (negro)",
          a: "Black"
        },
        {
          type: "order",
          q: "Ordena: Describing for ID: 'Tengo cabello castaño, ojos verdes, mido 5'8'",
          words: ["I", "have", "brown", "hair,", "green", "eyes,", "and", "I'm", "5'8\"", "tall."],
          a: "I have brown hair, green eyes, and I'm 5'8\" tall.",
          successtip: "✓ ID DESCRIPTION! Practical! 🪪 Driver's license, health card, passport = all ask for this info! Essential!"
        },
        {
          type: "mc",
          q: "Lost child report: 'She ___ 5 years old. She ___ blonde hair and blue eyes. She ___ wearing a red jacket.'",
          options: ["is, has, is", "has, has, has", "is, is, is"],
          a: "is, has, is",
          tip: "She IS 5 (age). She HAS hair/eyes (features). She IS wearing (clothes - continuous)!"
        },
        {
          type: "input",
          q: "Complete: 'My son ___ a birthmark on his arm.' (have/has)",
          a: "has"
        },
       {
        type: "mc",
        q: "Police description: 'He ___ about 30 years old. He ___ short black hair. He ___ approximately 6 feet tall.'",
        options: ["is, has, is", "has, has, has", "is, is, has"],
        a: "is, has, is",
        successtip: "✓ POLICE REPORT! Real situation! 👮 IS (age/height). HAS (hair). This vocab = emergency situations!"
      },
      {
        type: "order",
        q: "Doctor: '¿Tiene algún problema con sus ojos?'",
        words: ["Do", "you", "have", "any", "problems", "with", "your", "eyes?"],
        a: "Do you have any problems with your eyes?"
      },
      {
        type: "input",
        q: "Complete: 'I ___ glasses because my eyes are weak.' (wear - routine)",
        a: "wear"
      },
        {
          type: "mc",
          q: "Passport form: 'Distinguishing marks:' - Which is correct?",
          options: ["'I have a scar on my hand' ✓", "'I am have scar' ❌", "'My hand is scar' ❌"],
          a: "'I have a scar on my hand' ✓",
          tip: "Scar = cicatriz. 'I HAVE a scar ON my [body part]' = correct structure!"
        },
        {
          type: "mc",
          q: "Final mastery: 'My name ___ Ana. I ___ 32 years old. I ___ from Mexico. I ___ brown hair and brown eyes. My hair ___ long and curly. I ___ 5'4\" tall. I ___ glasses. I ___ a small scar on my left hand.'",
          options: [
            "is, am, am, have, is, am, wear, have",
            "am, am, have, have, is, have, wear, am",
            "is, have, am, have, are, am, wearing, am"
          ],
          a: "is, am, am, have, is, am, wear, have",
          successtip: "✓ COMPLETE PROFILE! Perfect description! 🎯 Name IS. I AM (age/from/tall). I HAVE (features). Hair IS (description). I WEAR (glasses). I HAVE (scar). You mastered body descriptions!"
             }
      ]
    }
  ],

    // ===================================================================
// READING SET: Sofia's Family (Physical Descriptions)
// ===================================================================
readingSet: {
  id: "m4-u10-reading",
  title: "Reading: Sofia's Family",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Sofia & Her Mother (preguntas 2, 3, 4 y 6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Sofia and her Mother**

Hi! My name is Sofia. Let me tell you about my family! I am 25 years old and I'm from Colombia. I have long brown hair and green eyes. I'm 5'6" tall, which is average height. 

My mother is beautiful. She is 50 years old now. She has short gray hair—she used to have dark brown hair like me, but now it's gray. She still has the same beautiful green eyes. She's shorter than me—she's only 5'2".`
    },
    {
      type: "mc",
      q: "En la frase 'I have long brown hair', ¿por qué usa 'have' y no 'has'?",
      options: [
        "Con el pronombre 'I' siempre se usa HAVE",
        "Debería ser 'I has'",
        "Es un error del texto"
      ],
      a: "Con el pronombre 'I' siempre se usa HAVE",
      tip: "Regla: I/You/We/They = HAVE. He/She/It = HAS. 💡",
      successtip: "✓ GRAMMAR RULE!\n'I have' es la forma correcta para la primera persona. 🎯"
    },
    {
      type: "mc",
      q: "En 'She has short gray hair', ¿por qué usa 'has'?",
      options: [
        "Porque con He/She/It siempre usamos HAS",
        "Debería ser 'have'",
        "Es un error de gramática"
      ],
      a: "Porque con He/She/It siempre usamos HAS",
      successtip: "✓ THIRD PERSON!\n'Has' es la conjugación obligatoria para la tercera persona del singular. ✨"
    },
    {
      type: "mc",
      q: "Comparando 'Her hair is gray' con 'She has gray hair', ¿cuál es correcta?",
      options: [
        "Ambas son correctas; son dos formas de describir",
        "Solo la primera es correcta",
        "Solo la segunda es correcta"
      ],
      a: "Ambas son correctas; son dos formas de describir",
      successtip: "✓ FLEXIBILITY! 🎨\n'My hair IS...' o 'I HAVE... hair'. ¡Ambas suenan muy naturales! 🗣️"
    },
    {
      type: "mc",
      q: "En la frase 'She's 5'2\" tall', ¿qué significa el apóstrofe en 'She's'?",
      options: [
        "Es la contracción de 'She is'",
        "Indica posesión",
        "Es una marca de plural"
      ],
      a: "Es la contracción de 'She is'",
      successtip: "✓ VERB TO BE!\nUsamos 'is' para hablar de la altura (height). 📏"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: The Father & Brother (preguntas 5 y 7)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Men in the Family**

My father is 52. He's tall—6 feet! He has almost no hair now, but in old photos he had black hair. He has brown eyes and a big nose. People say I have his nose!

My brother Carlos is 22. He's very tall like my dad—he's 6'2"! He has short black hair and brown eyes. He has big muscles because he goes to the gym every day.`
    },
    {
      type: "mc",
      q: "People say 'I have my mother's eyes'. ¿Qué indica el apóstrofe + 's' en mother's?",
      options: [
        "Posesión (los ojos pertenecientes a mi madre)",
        "Plural (muchas madres)",
        "La contracción de 'mother is'"
      ],
      a: "Posesión (los ojos pertenecientes a mi madre)",
      tip: "El apóstrofe 's indica pertenencia. 'Mother's eyes' = Los ojos de mamá. 👁️",
      successtip: "✓ POSSESSION!\nEstructura: Dueño + 's + lo que posee. 🎯"
    },
    {
      type: "mc",
      q: "La frase 'He has almost no hair now' describe que el padre:",
      options: [
        "Está calvo o tiene muy poco cabello",
        "Tiene mucho cabello",
        "Tiene el cabello canoso"
      ],
      a: "Está calvo o tiene muy poco cabello",
      successtip: "✓ BALDNESS! 👨‍🦲\n'Almost no hair' es una forma común de describir la calvicie. 💡"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Little Sister (pregunta 8)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Little Luna**

My little sister is only 10. Her name is Luna. She has long blonde hair—nobody knows where it came from! My parents both have dark hair. She has big blue eyes. She's very small for her age. Everyone says she looks like a little doll!`
    },
    {
      type: "mc",
      q: "En la frase 'Everyone says she looks like a doll', ¿qué significa 'looks like'?",
      options: [
        "Se parece a / tiene apariencia de",
        "Le gustan las muñecas",
        "Ella tiene una muñeca"
      ],
      a: "Se parece a / tiene apariencia de",
      tip: "'Look like' = parecerse físicamente a algo o alguien. 🧸",
      successtip: "✓ COMPARISON!\n'You look like your mom' = te pareces a tu mamá. ¡Muy útil! 👥"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Conclusion & Grammar Summary (preguntas 1, 9 y 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Beautiful Mix**

We all have different features, but we're a beautiful family. We're living in Toronto now, and we love it here!`
    },

    

    {
      type: "mc",
      q: "¿Cuántos verbos en Presente Simple utiliza Sofía aproximadamente?",
      options: [
        "Muchos (am, is, are, has, have, know, think, etc.)",
        "Solo 2 o 3",
        "Ninguno"
      ],
      a: "Muchos (am, is, are, has, have, know, think, etc.)",
      successtip: "✓ PRESENT SIMPLE!\nEs el tiempo verbal rey para las descripciones permanentes. 👑"
    },
    {
      type: "mc",
      q: "¿Por qué Sofía usa el verbo 'has' tantas veces en el texto?",
      options: [
        "Porque describe las características físicas de terceras personas (He/She)",
        "Es un error repetitivo",
        "Debería usar 'is' en lugar de 'has' siempre"
      ],
      a: "Porque describe las características físicas de terceras personas (He/She)",
      successtip: "✓ HAS vs HAVE!\nHas = He/She/It. Have = I/You/We/They. ¡Dominado! 🏆"
    },
    {
      type: "mc",
      q: "En conclusión, el texto demuestra:",
      options: [
        "La mezcla natural de 'To Be' + 'Have/Has' para descripciones completas",
        "Que solo se debe usar 'To Be' para describir",
        "Que 'Have' y 'Has' significan lo mismo que 'Is' y 'Are'"
      ],
      a: "La mezcla natural de 'To Be' + 'Have/Has' para descripciones completas",
      successtip: "✓ READING COMPLETE! 🎉\n¡Excelente! Has aprendido a describir personas combinando estados (IS) y rasgos (HAS). 🏆"
    }
  ]
},
    // SET 12: Unit Review
    reviewSet: {
      id: "m4-u10-review",
      title: "Set 12: Unit Review",
      category: "review",
      questions: [
        {
          type: "match",
          q: "Match structure to use:",
          pairs: [
            { left: "I/You/We/They + features", right: "HAVE" },
            { left: "He/She/It + features", right: "HAS" },
            { left: "My/Your/His/Her + body part", right: "IS/ARE + adjective" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "Rule for eyes/lips/teeth:",
          options: [
            "Always plural! 'My eyes ARE' ✓",
            "Always singular",
            "Can be either"
          ],
          a: "Always plural! 'My eyes ARE' ✓"
        },
        {
          type: "mc",
          q: "Rule for hair/nose/mouth:",
          options: [
            "Always singular! 'My hair IS' ✓",
            "Always plural",
            "Can be either"
          ],
          a: "Always singular! 'My hair IS' ✓"
        },
        {
          type: "input",
          q: "Complete: 'I ___ brown eyes.' (have/has)",
          a: "have"
        },
        {
          type: "input",
          q: "Complete: 'She ___ blonde hair.' (have/has)",
          a: "has"
        },
        {
          type: "order",
          q: "Ordena: 'Mi amiga tiene ojos hermosos y cabello largo'",
          words: ["My", "friend", "has", "beautiful", "eyes", "and", "long", "hair."],
          a: "My friend has beautiful eyes and long hair."
        },
        {
          type: "mc",
          q: "Irregular plural: foot →",
          options: ["feet", "foots", "foot"],
          a: "feet",
          tip: "Foot → Feet! Like tooth → teeth!"
        },
        {
          type: "mc",
          q: "ERROR: 'She have brown eyes' should be:",
          options: ["'She has brown eyes'", "'She having brown eyes'", "It's correct"],
          a: "'She has brown eyes'",
          tip: "She = HAS! Most common error!"
        },
        {
          type: "mc",
          q: "Word order: adjective + noun OR noun + adjective?",
          options: [
            "Adjective + noun ('long hair' ✓)",
            "Noun + adjective ('hair long')",
            "Both are correct"
          ],
          a: "Adjective + noun ('long hair' ✓)"
        },
        {
          type: "mc",
          q: "Final mastery: 'Hi! My name ___ Carlos. I ___ 30 years old. I ___ from Mexico. I ___ 5'10\" tall. I ___ black hair and brown eyes. My hair ___ short. My eyes ___ dark brown. I also ___ a small beard. People say I ___ handsome! I ___ very happy with how I look.'\n\nComplete with correct forms:",
          options: [
            "is, am, am, am, have, is, are, have, am, am",
            "am, am, have, have, have, are, are, has, have, have",
            "is, have, am, have, have, is, is, have, has, have"
          ],
          a: "is, am, am, am, have, is, are, have, am, am",
          successtip: "✓ UNIT 10 MASTERED! Body descriptions complete! 🎉 From zero to describing yourself and others naturally! You now know: body parts vocab, HAVE/HAS rules, IS/ARE with features, adjectives, colors, and real-life descriptions! You can fill forms, describe family, talk to doctors, and give police descriptions! THIS is practical English! 💪🔥👤"
     }
    ]
  }
};
